import { Fragment, useEffect, useState } from 'react';
import { pad, TICKER, GRAND_ALL } from '../campaign';
import { ChevronIcon, CloseIcon } from './Icons';
import rays from '../assets/rays.webp';
import storybg from '../assets/storybg.webp';
import gem from '../assets/gem.webp';
import chest from '../assets/chest.webp';
import logo from '../assets/logo.webp';
import ps5 from '../assets/ps5.webp';
import iphone from '../assets/iphone.webp';
import watch from '../assets/watch.webp';
import lantern from '../assets/lantern.webp';
import chain from '../assets/chain.webp';
import flourish from '../assets/flourish.webp';
import samsung from '../assets/prizes/samsung.webp';
import airpods from '../assets/prizes/airpods.webp';
import redmi from '../assets/prizes/redmi.webp';
import opwatch from '../assets/prizes/opwatch.webp';
import jblflip from '../assets/prizes/jblflip.webp';
import sony from '../assets/prizes/sony.webp';
import jblgo from '../assets/prizes/jblgo.webp';
import fastrack from '../assets/prizes/fastrack.webp';
import nordbuds from '../assets/prizes/nordbuds.webp';
import ambrane from '../assets/prizes/ambrane.webp';

/* artwork by crop kind — `watch2` is the same watch render with a tighter crop */
const ART = { gem, chest, logo, ps5, iphone, watch, watch2: watch };
/* the other grand prizes are plain product renders (crop kind `prod`) */
const PRODUCTS = { samsung, airpods, redmi, opwatch, jblflip, sony, jblgo, fastrack, nordbuds, ambrane };

/* the crop kind + image for a prize's `art` key (API or campaign.js) */
export function prizeArt(art) {
  if (ART[art]) return { kind: art };
  if (PRODUCTS[art]) return { kind: 'prod', src: PRODUCTS[art] };
  return null;
}

/* a prize's artwork: by its own `art` key, else the campaign default for that
   name; a prize we have no render for shows its rank instead */
export function PrizeArt({ prize, className }) {
  const fallback = GRAND_ALL.find((g) => g.name === prize.name);
  const art = prizeArt(prize.art) || prizeArt(fallback && fallback.art);
  if (!art) return <div className={`gq-rank${className ? ' ' + className : ''}`}>{(prize.label || '').replace('Grand ', '')}</div>;
  return <Crop kind={art.kind} src={art.src} className={className} />;
}

/* A fixed-size window onto a piece of artwork. The `kind` picks the image and
   the crop percentages (see .gq-crop.gq-<kind> img); extra classes position it.
   `src` overrides the image (product renders use kind `prod`). */
export function Crop({ kind, src, className = '', alt = '', children }) {
  return (
    <div className={`gq-crop gq-${kind}${className ? ' ' + className : ''}`}>
      <img src={src || ART[kind]} alt={alt} />
      {children}
    </div>
  );
}

/* radial light rays, multiplied over the section behind them */
export function Rays({ className = '' }) {
  return (
    <div className={`gq-rays${className ? ' ' + className : ''}`}>
      <img src={rays} alt="" />
    </div>
  );
}

/* the slowly turning purple ray backdrop behind the hero and every full-screen overlay */
export function BgRot() {
  return (
    <div className="gq-bgrot">
      <img src={storybg} alt="" />
    </div>
  );
}

/* true for `ms` after mount (or after `key` changes) — drives the one-shot
   entrance animations the prototype plays with a temporary class */
export function useEntering(ms, key = 0) {
  const [on, setOn] = useState(true);
  useEffect(() => {
    setOn(true);
    const t = setTimeout(() => setOn(false), ms);
    return () => clearTimeout(t);
  }, [ms, key]);
  return on;
}

/* Hanging lantern (or bare chain) that sways on its own timing. `pos` is the
   slot class (l1…l8) that places it; `chain` swaps in the chain artwork. */
export function Lantern({ pos, chain: chainKind, mirror = false }) {
  const cls = ['gq-lant', `gq-${pos}`, chainKind ? `gq-${chainKind}` : '', mirror ? 'gq-m' : '']
    .filter(Boolean).join(' ');
  return (
    <div className={cls}>
      <div className="gq-sw"><img src={chainKind ? chain : lantern} alt="" /></div>
    </div>
  );
}

/* The pale marquee strip over the top edge of the streak sheet; the text is
   rendered twice so the 46s loop is seamless. `fixed` renders the copy that
   pins to the top of the screen once the band scrolls up to it. */
export function TickerBand({ fixed = false, innerRef }) {
  return (
    <div className={fixed ? 'gq-tickfixed' : 'gq-tickband'} ref={innerRef} aria-hidden="true">
      <div className="gq-tkwrap">
        <div className="gq-tk">
          {[0, 1].map((k) => (
            <span key={k}>{TICKER.map((t) => <Fragment key={t}>{t}<i /></Fragment>)}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Centred section title flanked by two gold flourishes. Which flourish is
   mirrored differs per section in the design, hence `variant`. */
export function SectionTitle({ variant, children }) {
  const mirrorA = variant === 'streaks' || variant === 'bonus-t';
  return (
    <div className={`gq-sec gq-${variant}`}>
      <div className={`gq-fl gq-a${mirrorA ? ' gq-m' : ''}`}><img src={flourish} alt="" /></div>
      <div className="gq-ttl">{children}</div>
      <div className={`gq-fl gq-b${mirrorA ? '' : ' gq-m'}`}><img src={flourish} alt="" /></div>
    </div>
  );
}

/* Gold sparks thrown out from (x, y) in stage px; render each as
   <i className="gq-spark" style={s.style} /> and drop them after ~1.3s. */
export function makeSparks(x, y, n) {
  return Array.from({ length: n }, (_, i) => {
    const a = Math.random() * Math.PI * 2;
    const r = 70 + Math.random() * 120;
    const sz = 3 + Math.random() * 5;
    return {
      id: i,
      style: {
        left: x + Math.random() * 16 - 8,
        top: y + Math.random() * 16 - 8,
        width: sz,
        height: sz,
        background: Math.random() < 0.6 ? '#FBCC65' : '#F6D5AC',
        boxShadow: `0 0 ${sz * 2}px #FBCC65`,
        '--dx': `${Math.cos(a) * r}px`,
        '--dy': `${Math.sin(a) * r - 30}px`,
      },
    };
  });
}

/* The full-screen overlays are laid out on the 812px-tall design with their
   CTA block anchored to the bottom, so on a shorter stage (Android with
   system bars, small phones) that block would ride up into the content. This
   fits the content "scene" instead: it first gives up the slack above it
   (down to `minTop`), then scales down from its top edge until it clears the
   `bottom` block. Positions are stage px; `style` goes on the scene element.
   - designTop : scene top on the 812px design
   - height    : scene height on the design
   - minTop    : how high the scene may move (below the header)
   - bottom    : space the bottom block needs (CTA + margins + a gap) */
export function fitScene(stageHeight, { designTop, height, minTop, bottom }) {
  const avail = stageHeight - minTop - bottom;
  let top = designTop;
  let scale = 1;
  if (avail < height) {
    top = minTop;
    scale = Math.max(0.5, avail / height); // never shrink past half — overlap beats unreadable
  } else if (avail < height + (designTop - minTop)) {
    top = minTop + (avail - height);
  }
  return { top, scale, style: { top, transform: scale < 1 ? `scale(${scale})` : undefined } };
}

/* maps a point on the design to the fitted scene */
export const inScene = (fit, designY, sceneDesignTop) => fit.top + (designY - sceneDesignTop) * fit.scale;

/* The gold pill CTA. `disabled` renders the muted locked look, `done` the
   outlined "complete" look; both are non-interactive. */
export function GoldButton({ className = '', disabled = false, done = false, children, ...rest }) {
  const cls = ['gq-goldbtn', disabled ? 'gq-disabled' : '', done ? 'gq-done' : '', className]
    .filter(Boolean).join(' ');
  return (
    <button type="button" className={cls} disabled={disabled || done} {...rest}>
      <span className="gq-lbl">{children}</span>
    </button>
  );
}

/* Gems / Chests summary tile (hub hero and the rewards page). `chip` is the
   optional status strip at the bottom: { text, muted, claimable } — gold by
   default, `muted` for the quieter reserved / draw states, `claimable` pulses
   it (the pre-launch "Claim 5 Gems" call). `flash` plays the gold flash and
   count bump after the Gems were reserved. Without `onClick` it's a static
   tile with no chevron. */
export function StatBox({ kind, num, label, chip, flash = false, onClick }) {
  const Tag = onClick ? 'button' : 'div';
  const cls = ['gq-sbox', `gq-${kind}`, chip && chip.claimable ? 'gq-claimable' : '', flash ? 'gq-flash' : '']
    .filter(Boolean).join(' ');
  return (
    <Tag type={onClick ? 'button' : undefined} className={cls} onClick={onClick}>
      <div className="gq-main">
        <Crop kind={kind === 'gems' ? 'gem' : 'chest'} className="gq-art" />
        <div className="gq-txt">
          <div className={`gq-num${flash ? ' gq-bump' : ''}`}>{pad(num)}</div>
          <div className="gq-lab">{label}</div>
        </div>
        {onClick && <div className="gq-chev"><ChevronIcon /></div>}
      </div>
      {chip && <div className={`gq-chip${chip.muted ? ' gq-muted' : ''}`}>{chip.text}</div>}
    </Tag>
  );
}

/* Bottom sheet over the hub (prizes, terms, draw results). Rendered outside
   the page in a zoomed wrapper like the toast, so it's fixed to the viewport
   and centred with the stage. It slides in on mount and out when `open`
   turns false; the parent drops it after SHEET_EXIT_MS so the exit plays.
   `stageHeight` caps it at 92% of the viewport (stage px). `className`
   styles a variant; `backdrop` puts the turning ray backdrop behind it. */
export const SHEET_EXIT_MS = 450;
export function Sheet({ open, label, stageHeight = 812, onClose, className = '', backdrop = false, children }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!open) { setShown(false); return undefined; }
    /* mount at translateY(105%) first so the slide-in transitions */
    const raf = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const maxH = Math.round(stageHeight * 0.92);
  return (
    <>
      <div className={`gq-scrim${shown ? ' gq-show' : ''}`} onClick={onClose} />
      <div className={`gq-sheet${className ? ' ' + className : ''}${shown ? ' gq-show' : ''}`} role="dialog" aria-modal="true" aria-label={label} style={{ maxHeight: maxH }}>
        {backdrop && <BgRot />}
        <div className="gq-handle" />
        <button type="button" className="gq-sh-close" onClick={onClose} aria-label="Close"><CloseIcon /></button>
        <div className="gq-sh-body" style={{ maxHeight: maxH }}>{children}</div>
      </div>
    </>
  );
}
